import { LayoutDashboard, Receipt, Settings, Store } from 'lucide-react';

/**
 * Role-aware profile dropdown for the sidebar's top-right avatar.
 *
 * The chat client is a separate origin (chat.z2market.com), so every link must be
 * absolute - a relative "/buyer-dashboard/orders" would 404 on the subdomain.
 *
 * Routes come from web/assets/js/routes-config.js, the same source the main site
 * uses, so they cannot drift:
 *   BUYER_DASHBOARD/BUYER_ORDERS/BUYER_SETTINGS
 *   SELLER_DASHBOARD/SELLER_ORDERS/SELLER_SETTINGS
 */
const MAIN_SITE = 'https://z2market.com';

const LINKS = {
  BUYER: [
    { label: 'Dashboard', href: `${MAIN_SITE}/buyer-dashboard/overview`, Icon: LayoutDashboard },
    { label: 'Orders', href: `${MAIN_SITE}/buyer-dashboard/orders`, Icon: Receipt },
    { label: 'Settings', href: `${MAIN_SITE}/buyer-dashboard/settings`, Icon: Settings },
  ],
  SELLER: [
    { label: 'Dashboard', href: `${MAIN_SITE}/seller-dashboard/overview`, Icon: LayoutDashboard },
    { label: 'Orders', href: `${MAIN_SITE}/seller-dashboard/orders`, Icon: Receipt },
    // routes-config.js SELLER_SETTINGS
    { label: 'Settings', href: `${MAIN_SITE}/seller-dashboard/preferences.html`, Icon: Settings },
  ],
};

export function profileLinksFor(role) {
  return LINKS[role] ?? LINKS.BUYER;
}

export default function ProfileMenu({ open, onClose, user }) {
  if (!open) return null;

  const links = profileLinksFor(user?.role);
  const displayName = user?.displayName || user?.username || 'Your account';

  return (
    <>
      <button
        type="button"
        aria-label="Close profile menu"
        onClick={onClose}
        tabIndex={-1}
        className="fixed inset-0 z-40 cursor-default"
      />

      <div
        role="menu"
        aria-label="Profile menu"
        className="animate-z2-drop absolute top-[54px] right-2 z-50 w-[220px] overflow-hidden rounded-2xl border border-line bg-surface shadow-lift"
      >
        <div className="border-b border-line px-3.5 py-2.5">
          <p className="truncate text-[12.5px] font-semibold text-ink">{displayName}</p>
          {user?.email && <p className="truncate text-[11px] text-ink-3">{user.email}</p>}
        </div>

        <ul className="py-1">
          {links.map(({ label, href, Icon }) => (
            <li key={label}>
              <a
                role="menuitem"
                href={href}
                className="flex items-center gap-2.5 px-3.5 py-2 text-[12.5px] font-medium text-ink-2 transition hover:bg-surface-3 hover:text-ink"
              >
                <Icon size={15} className="shrink-0 text-accent" />
                {label}
              </a>
            </li>
          ))}
        </ul>

        <div className="border-t border-line py-1">
          <a
            role="menuitem"
            href={MAIN_SITE}
            className="flex items-center gap-2.5 px-3.5 py-2 text-[12.5px] font-medium text-ink-2 transition hover:bg-surface-3 hover:text-ink"
          >
            <Store size={15} className="shrink-0 text-ink-3" />
            Back to marketplace
          </a>
        </div>
      </div>
    </>
  );
}
